import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";

const DeployAuthContext = createContext(null);

const deployHttp = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * RivieraDeploy dashboard session — GET /api/deploy/auth/session (RD-0048).
 * Separate from main site AuthContext; uses deployCustomerId cookie session.
 */
export function DeployAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  /** @type {[string, import('react').Dispatch<import('react').SetStateAction<string>>]} */
  const [deploySupportEmail, setDeploySupportEmail] = useState("");
  const [globalKillSwitchActive, setGlobalKillSwitchActive] = useState(false);
  const [dashboardBanner, setDashboardBanner] = useState(null);
  /** @type {[number, import('react').Dispatch<import('react').SetStateAction<number>>]} */
  const [apiKeyRotationWarnDays, setApiKeyRotationWarnDays] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  /** Set when a signup is refused for capacity — rendered as a notice, never as an error. */
  const [capacityMessage, setCapacityMessage] = useState("");

  const refreshSession = useCallback(async () => {
    const t0 = Date.now();
    console.log(`[${new Date().toISOString()}] [DeployAuthContext] refreshSession start`);
    setIsLoading(true);
    setError("");
    try {
      const { data } = await deployHttp.get("/api/deploy/auth/session");
      console.log(
        `[${new Date().toISOString()}] [DeployAuthContext] session response ms=${Date.now() - t0} authenticated=${data.authenticated}`
      );
      setGlobalKillSwitchActive(Boolean(data.globalKillSwitchActive));
      setDashboardBanner(
        data.dashboardBanner && typeof data.dashboardBanner === "object" ? data.dashboardBanner : null
      );
      setDeploySupportEmail(
        typeof data.deploySupportEmail === "string" && data.deploySupportEmail.trim()
          ? data.deploySupportEmail.trim()
          : ""
      );
      const warn =
        typeof data.apiKeyRotationWarnDays === "number" && data.apiKeyRotationWarnDays > 0
          ? data.apiKeyRotationWarnDays
          : 0;
      setApiKeyRotationWarnDays(warn);
      if (data.success && data.authenticated && data.customer) {
        setCustomer(data.customer);
      } else {
        setCustomer(null);
      }
    } catch (e) {
      console.error(
        `[${new Date().toISOString()}] [DeployAuthContext] refreshSession error`,
        e?.message
      );
      setCustomer(null);
      setDeploySupportEmail("");
      setDashboardBanner(null);
      setApiKeyRotationWarnDays(0);
      setError("Could not verify deploy session.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const login = useCallback(async (email, password) => {
    const t0 = Date.now();
    console.log(`[${new Date().toISOString()}] [DeployAuthContext] login attempt`);
    setError("");
    try {
      const { data } = await deployHttp.post("/api/deploy/auth/login", { email, password });
      console.log(`[${new Date().toISOString()}] [DeployAuthContext] login ok ms=${Date.now() - t0}`);
      if (data.success && data.customer) {
        setCustomer(data.customer);
        setGlobalKillSwitchActive(Boolean(data.globalKillSwitchActive));
        setDashboardBanner(
          data.dashboardBanner && typeof data.dashboardBanner === "object" ? data.dashboardBanner : null
        );
        setDeploySupportEmail(
          typeof data.deploySupportEmail === "string" && data.deploySupportEmail.trim()
            ? data.deploySupportEmail.trim()
            : ""
        );
        const warn =
          typeof data.apiKeyRotationWarnDays === "number" && data.apiKeyRotationWarnDays > 0
            ? data.apiKeyRotationWarnDays
            : 0;
        setApiKeyRotationWarnDays(warn);
        return true;
      }
      setError(data.error || "Login failed");
      return false;
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "Login failed";
      setError(e.response?.data?.supportReference ? `${msg} Support reference: ${e.response.data.supportReference}` : msg);
      console.error(`[${new Date().toISOString()}] [DeployAuthContext] login error`, msg);
      return false;
    }
  }, []);

  const register = useCallback(async (payload) => {
    const { email, password, company, termsAccepted, privacyAccepted, inviteToken } = payload;
    const t0 = Date.now();
    console.log(`[${new Date().toISOString()}] [DeployAuthContext] register attempt`);
    setError("");
    try {
      const { data } = await deployHttp.post("/api/deploy/auth/register", {
        email,
        password,
        company: company || "",
        termsAccepted: termsAccepted === true,
        privacyAccepted: privacyAccepted === true,
        inviteToken: inviteToken || undefined,
      });
      console.log(`[${new Date().toISOString()}] [DeployAuthContext] register ok ms=${Date.now() - t0}`);
      if (data.success && data.customer) {
        setCustomer(data.customer);
        setGlobalKillSwitchActive(Boolean(data.globalKillSwitchActive));
        setDashboardBanner(
          data.dashboardBanner && typeof data.dashboardBanner === "object" ? data.dashboardBanner : null
        );
        setDeploySupportEmail(
          typeof data.deploySupportEmail === "string" && data.deploySupportEmail.trim()
            ? data.deploySupportEmail.trim()
            : ""
        );
        const warn =
          typeof data.apiKeyRotationWarnDays === "number" && data.apiKeyRotationWarnDays > 0
            ? data.apiKeyRotationWarnDays
            : 0;
        setApiKeyRotationWarnDays(warn);
        return true;
      }
      setError(data.error || "Registration failed");
      return false;
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "Registration failed";
      // Hitting the signup cap is not a failed registration — the customer did nothing
      // wrong and there is nothing to correct and retry. Keep it out of `error` (which
      // renders red) so the page can show the capacity notice instead.
      if (e.response?.data?.code === "SIGNUP_CAP_REACHED") {
        setCapacityMessage(msg);
        console.log(`[${new Date().toISOString()}] [DeployAuthContext] register blocked — at capacity`);
        return false;
      }
      setError(e.response?.data?.supportReference ? `${msg} Support reference: ${e.response.data.supportReference}` : msg);
      console.error(`[${new Date().toISOString()}] [DeployAuthContext] register error`, msg);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    console.log(`[${new Date().toISOString()}] [DeployAuthContext] logout`);
    try {
      await deployHttp.post("/api/deploy/auth/logout");
    } catch (e) {
      console.error(`[${new Date().toISOString()}] [DeployAuthContext] logout error`, e?.message);
    }
    setCustomer(null);
    setDeploySupportEmail("");
    setDashboardBanner(null);
    setApiKeyRotationWarnDays(0);
  }, []);

  const value = useMemo(
    () => ({
      customer,
      deploySupportEmail,
      globalKillSwitchActive,
      dashboardBanner,
      apiKeyRotationWarnDays,
      isAuthenticated: !!customer,
      isLoading,
      error,
      setError,
      capacityMessage,
      setCapacityMessage,
      refreshSession,
      login,
      register,
      logout,
    }),
    [
      customer,
      deploySupportEmail,
      globalKillSwitchActive,
      dashboardBanner,
      apiKeyRotationWarnDays,
      isLoading,
      error,
      capacityMessage,
      refreshSession,
      login,
      register,
      logout,
    ]
  );

  return <DeployAuthContext.Provider value={value}>{children}</DeployAuthContext.Provider>;
}

export function useDeployAuth() {
  const ctx = useContext(DeployAuthContext);
  if (!ctx) {
    throw new Error("useDeployAuth must be used within DeployAuthProvider");
  }
  return ctx;
}
